import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Home as HomeIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import axiosPublic from '../../api/axiosPublic';
import { normalizeApiError } from '../../api/errorUtils';

const DocumentVerifyPage = () => {
  const { verificationCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    const verify = async () => {
      if (!verificationCode) {
        setError('Verification code is missing.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const response = await axiosPublic.get(`/api/documents/verify/${verificationCode}`);
        setData(response.data || null);
      } catch (err) {
        const normalized = normalizeApiError(err, 'Failed to verify document.');
        setError(normalized.message);
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, [verificationCode]);

  const isVerified = Boolean(data?.verified);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: { xs: 3, md: 6 },
        background:
          'radial-gradient(circle at 15% -5%, rgba(30, 64, 175, 0.15), transparent 38%), radial-gradient(circle at 96% 0%, rgba(15, 118, 110, 0.14), transparent 36%), linear-gradient(180deg, #f4f8ff 0%, #ffffff 58%, #f8fcff 100%)',
      }}
    >
      <Container maxWidth="md">
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <VerifiedIcon color={isVerified ? 'success' : 'warning'} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Document Verification
                </Typography>
              </Stack>
              <Button component={RouterLink} to="/" startIcon={<HomeIcon />} variant="outlined" size="small">
                Home
              </Button>
            </Stack>

            {loading ? (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Verifying document...
                </Typography>
              </Stack>
            ) : null}

            {!loading && error ? (
              <Alert severity="error" icon={<ErrorOutlineIcon />} variant="outlined">
                {error}
              </Alert>
            ) : null}

            {!loading && !error && data ? (
              <Stack spacing={1.5}>
                <Alert
                  severity={isVerified ? 'success' : 'warning'}
                  icon={isVerified ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                >
                  {data.message || (isVerified ? 'Document is valid.' : 'Document is not valid.')}
                </Alert>
                <Divider />
                <Grid container spacing={1.2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Document
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {data.documentTitle || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {(data.documentType || 'N/A').replace(/_/g, ' ')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      School
                    </Typography>
                    <Typography variant="body1">{data.schoolName || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Verification Code
                    </Typography>
                    <Typography variant="body1">{data.verificationCode || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Reference
                    </Typography>
                    <Typography variant="body1">{data.referenceNumber || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.2 }}>
                      <Chip
                        size="small"
                        label={data.status || (isVerified ? 'ACTIVE' : 'INVALID')}
                        color={isVerified ? 'success' : 'warning'}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Issued At
                    </Typography>
                    <Typography variant="body1">
                      {data.issuedAt ? new Date(data.issuedAt).toLocaleString() : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Expires At
                    </Typography>
                    <Typography variant="body1">
                      {data.expiresAt ? new Date(data.expiresAt).toLocaleString() : 'No expiry'}
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>
            ) : null}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default DocumentVerifyPage;

